import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { MarkdownModule } from 'ngx-markdown';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { SharedModule } from 'src/app/shared/shared.module';
import { CustomReuseStrategy } from '../../shared/strategy/custom-reuse-strategy.service';
import { ReactiveFormsUiModule } from '../reactive-forms-ui/reactive-forms-ui.module';
import { ManagedServicesComponent } from './components/managed-services.component';
import { MspConfigBlockedTimesComponent } from './components/msp-configuration/components/msp-config-blocked-times/msp-config-blocked-times.component';
import { MspConfigCardWrapperComponent } from './components/msp-configuration/components/msp-config-card-wrapper/msp-config-card-wrapper.component';
import { MspConfigModuleApprovalComponent } from './components/msp-configuration/components/msp-config-module-approval/msp-config-module-approval.component';
import { MspConfigurationComponent } from './components/msp-configuration/msp-configuration.component';
import { PortalsComponent } from './components/portals/portals.component';
import { AddTicketNoteDialogComponent } from './components/ticket-detail/components/add-ticket-note-dialog/add-ticket-note-dialog.component';
import { CloseTicketDialogComponent } from './components/ticket-detail/components/close-ticket-dialog/close-ticket-dialog.component';
import { SlaTooltipComponent } from './components/ticket-detail/components/sla-tooltip/sla-tooltip.component';
import { TicketAttachmentThumbnailComponent } from './components/ticket-detail/components/ticket-attachment-thumbnail/ticket-attachment-thumbnail.component';
import { TicketDetailCardDetailsComponent } from './components/ticket-detail/components/ticket-detail-card-details/ticket-detail-card-details.component';
import { TicketDetailHeaderComponent } from './components/ticket-detail/components/ticket-detail-header/ticket-detail-header.component';
import { UploadDragAndDropComponent } from './components/ticket-detail/components/upload-drag-and-drop/upload-drag-and-drop.component';
import { TicketDetailComponent } from './components/ticket-detail/ticket-detail.component';
import { TicketsTableComponent } from './components/tickets-table/tickets-table.component';
import { TicketDetailParentComponent } from './components/ticket-detail/components/ticket-detail-parent/ticket-detail-parent.component';
import { TicketDetailChildrenComponent } from './components/ticket-detail/components/ticket-detail-children/ticket-detail-children.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { DashboardsComponent } from './components/dashboards/dashboards.component';
import { NgChartsModule } from 'ng2-charts';
import { MspSubmoduleGuard } from './guards/msp-submodule.guard';
import { MultiDashboardComponent } from './components/multi-dashboard/multi-dashboard.component';
import { MultiDashboardV2Component } from './components/multi-dashboard-v2/multi-dashboard-v2.component';
import { DashboardPrioritiesComponent } from './components/multi-dashboard-v2/dashboard-priorities/dashboard-priorities.component';
import { DashboardDateRangesComponent } from './components/multi-dashboard-v2/dashboard-date-ranges/dashboard-date-ranges.component';
import { DashboardThirtyDaysChartComponent } from './components/multi-dashboard-v2/dashboard-thirty-days-chart/dashboard-thirty-days-chart.component';
import { DashboardSummariesComponent } from './components/multi-dashboard-v2/dashboard-summaries/dashboard-summaries.component';
import { TicketsTableV2Component } from './components/tickets-table-v2/tickets-table-v2.component';
import { TicketsTableContainerComponent } from './components/tickets-table-container/tickets-table-container.component';
import { ReactiveFiltersModule } from '../reactive-filters/reactive-filters.module';
import { TicketAnalysisComponent } from './components/ticket-analysis/ticket-analysis.component';
import { TicketAnalysisCategorizationComponent } from './components/ticket-analysis/categorization/ticket-analysis-categorization.component';
import { TicketAnalysisSlaComponent } from './components/ticket-analysis/sla/ticket-analysis-sla.component';
import { TicketAnalysisVarianceComponent } from './components/ticket-analysis/variance/ticket-analysis-variance.component';
import { TicketAnalysisSmilebackComponent } from './components/ticket-analysis/smileback/ticket-analysis-smileback.component';

const childrenRoutes: any = [
  {
    path: '',
    redirectTo: 'portals',
    pathMatch: 'full',
  },
  {
    path: 'portals',
    canActivate: [MspSubmoduleGuard],
    data: { submodule: 'Portals' },
    component: PortalsComponent,
  },
  {
    path: 'tickets/:ticketNumber',
    canActivate: [MspSubmoduleGuard],
    data: { submodule: 'Tickets' },
    component: TicketDetailComponent,
  },
  {
    path: 'tickets',
    canActivate: [MspSubmoduleGuard],
    data: { submodule: 'Tickets', key: 'managed-services-tickets' },
    component: TicketsTableContainerComponent,
  },
  {
    path: 'settings',
    canActivate: [MspSubmoduleGuard],
    data: { submodule: 'Settings' },
    component: MspConfigurationComponent,
  },
  {
    path: 'dashboards',
    canActivate: [MspSubmoduleGuard],
    data: { submodule: 'Dashboards' },
    component: DashboardsComponent,
    children: [
      {
        path: ':dashboardType',
        component: MultiDashboardV2Component,
      },
    ],
  },
  {
    path: 'ticket-analysis',
    canActivate: [MspSubmoduleGuard],
    data: { submodule: 'Ticket Analysis' },
    component: TicketAnalysisComponent,
    children: [
      { path: '', redirectTo: 'sla', pathMatch: 'full' },
      { path: 'sla', component: TicketAnalysisSlaComponent },
      { path: 'categorization', component: TicketAnalysisCategorizationComponent },
      { path: 'variance', component: TicketAnalysisVarianceComponent },
      { path: 'smileback', component: TicketAnalysisSmilebackComponent },
    ],
  },
];

const routing = RouterModule.forChild([
  {
    path: '',
    component: ManagedServicesComponent,
    children: childrenRoutes,
  },
  {
    path: 'pulse',
    canActivate: [MspSubmoduleGuard],
    data: { submodule: 'Pulse' },
    loadChildren: () => import('../pulse/pulse.module').then((m) => m.PulseModule),
  },
]);

const materialModules = [
  MatTabsModule,
  MatProgressSpinnerModule,
  MatTableModule,
  MatPaginatorModule,
  MatSortModule,
  MatIconModule,
  MatDialogModule,
  MatButtonModule,
  MatButtonToggleModule,
  MatChipsModule,
  MatSlideToggleModule,
  MatSelectModule,
  MatTooltipModule,
  MatProgressBarModule,
  MatAutocompleteModule,
  MatCardModule,
];

@NgModule({
  providers: [{ provide: RouteReuseStrategy, useClass: CustomReuseStrategy }],
  declarations: [
    ManagedServicesComponent,
    TicketsTableComponent,
    PortalsComponent,
    TicketDetailComponent,
    TicketDetailHeaderComponent,
    SlaTooltipComponent,
    TicketDetailCardDetailsComponent,
    MspConfigurationComponent,
    MspConfigCardWrapperComponent,
    MspConfigModuleApprovalComponent,
    MspConfigBlockedTimesComponent,
    AddTicketNoteDialogComponent,
    TicketAttachmentThumbnailComponent,
    UploadDragAndDropComponent,
    CloseTicketDialogComponent,
    TicketDetailParentComponent,
    TicketDetailChildrenComponent,
    DashboardsComponent,
    MultiDashboardComponent,
    MultiDashboardV2Component,
    DashboardPrioritiesComponent,
    DashboardDateRangesComponent,
    DashboardThirtyDaysChartComponent,
    DashboardSummariesComponent,
    TicketsTableV2Component,
    TicketsTableContainerComponent,
    TicketAnalysisComponent,
    TicketAnalysisCategorizationComponent,
    TicketAnalysisSlaComponent,
    TicketAnalysisVarianceComponent,
    TicketAnalysisSmilebackComponent,
  ],
  exports: [TicketDetailHeaderComponent],
  imports: [
    CommonModule,
    SharedModule,
    routing,
    ...materialModules,
    ReactiveFormsUiModule,
    NgxSkeletonLoaderModule,
    FormsModule,
    ReactiveFormsModule,
    MarkdownModule.forRoot(),
    NgChartsModule,
    ReactiveFiltersModule,
  ],
})
export class ManagedServicesModule {}
